import TrustBar from "../../components/feature/TrustBar";
import SectionPageHero from "../../components/feature/SectionPageHero";
import ProcessSection from "../home/components/ProcessSection";
import WhyChooseUs from "../home/components/WhyChooseUs";
import ServiceAreasSection from "../home/components/ServiceAreasSection";
import TestimonialsSection from "../home/components/TestimonialsSection";
import FinalCTASection from "../home/components/FinalCTASection";
import { useState } from "react";

const ABOUT_FAQS = [
  { id: 1, q: "How long has Jerry's Plumbing been in business?", a: "Jerry's Plumbing, Heating & Welding has been serving the Dallas–Fort Worth metroplex for over 35 years. Founded by a Texas Master Plumber, we have built a reputation for reliability, honesty, and quality workmanship across Cedar Hill and all DFW communities." },
  { id: 2, q: "Who owns Jerry's Plumbing & Heating?", a: "Jerry's is owned and operated by a licensed Texas Master Plumber (LIC #M12160) with decades of hands-on experience. This means every job meets the highest standards of quality, code compliance, and professional workmanship — guaranteed." },
  { id: 3, q: "Why choose a local plumbing company over a national chain?", a: "Local companies like Jerry's provide faster response times, understand DFW-specific plumbing systems and soil conditions, and offer more personalized service. You talk to the same team every time — not a call center — and we stand behind every repair we make." },
  { id: 4, q: "Is Jerry's Plumbing licensed and insured in Texas?", a: "Yes. Jerry's holds Texas Master Plumber License #M12160 and is fully insured. Every technician is background-checked and trained. You can trust that all work meets Texas state plumbing codes and industry standards." },
  { id: 5, q: "What types of properties does Jerry's service?", a: "We serve both residential homeowners and commercial businesses throughout DFW. From single-family homes to apartment complexes, restaurants needing grease trap installation, and large commercial buildings — Jerry's has the expertise and licensing to handle any property type." },
  { id: 6, q: "Do you offer free estimates for plumbing work?", a: "Yes — we provide free estimates and transparent written quotes before any work begins. You'll always know the cost upfront with no hidden fees. Call (817) 832-4968 to schedule a free assessment at your property." },
];

const VALUES = [
  { icon: "ri-shield-check-line", title: "Licensed & Insured", desc: "TX Master Plumber #M12160 — every job meets state code and industry standards." },
  { icon: "ri-handshake-line", title: "Honest Approach", desc: "No upselling, no fear tactics. Straight answers and transparent quotes before any work begins." },
  { icon: "ri-tools-line", title: "Quality Materials", desc: "Only proven, high-quality parts for repairs that last — no shortcuts, ever." },
  { icon: "ri-user-heart-line", title: "Customer First", desc: "From first call to final walkthrough — your satisfaction is our only measure of success." },
];

export default function AboutPage() {
  const [openId, setOpenId] = useState<number | null>(1);

  return (
    <main className="pt-[100px]">
      {/* 1. Hero */}
      <SectionPageHero
        badge="About Jerry's — DFW Since 1989"
        headline={<>A Local Company Built on <span className="text-[#E8500A]">Reliable Service &amp; Quality Work</span></>}
        subheadline="Jerry's Plumbing, Heating & Welding has served the Dallas–Fort Worth metroplex for over 35 years with honesty, expertise, and a commitment to long-term solutions — not temporary fixes."
        imageUrl="https://public.readdy.ai/ai/img_res/6d5c6062c22231b45a322604567c3df9.jpg"
        stats={[{ value: "35+", label: "Years in DFW" }, { value: "24/7", label: "Emergency Service" }, { value: "100%", label: "Locally Owned" }]}
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Our Story (Adapted Content) */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-[#E8500A] font-bold text-xs tracking-widest uppercase mb-3">Our Story</p>
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>
                Built on Trust.<br /><span className="text-[#E8500A]">Proven by Results.</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p><strong className="text-gray-900">Jerry's Plumbing, Heating & Welding</strong> has been a premier residential and commercial plumbing and welding company in the Dallas and Fort Worth areas for many years. We are owned by a <strong className="text-gray-900">Master Plumber with decades of experience</strong> in the field.</p>
                <p>We provide plumbing, heating, and welding services for a wide range of customers who&apos;ve come to depend on our high-quality professionalism, superior workmanship, and unsurpassed customer service across all of DFW.</p>
                <p>We have been known as the premier service for <strong className="text-gray-900">grease trap installations</strong> and <strong className="text-gray-900">slab leak repair</strong> in the Fort Worth and Dallas metroplex for over 35 years. Our reputation is built one job at a time.</p>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-gray-100">
                {[{ value: "35+", label: "Years in Business" }, { value: "24/7", label: "Emergency Service" }, { value: "100%", label: "Locally Owned" }].map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="text-3xl font-black text-[#E8500A]" style={{ fontFamily: "'Barlow', sans-serif" }}>{s.value}</p>
                    <p className="text-xs text-gray-500 font-semibold mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {VALUES.map((v) => (
                  <div key={v.title} className="bg-[#F8F7F4] rounded-2xl p-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl mb-4">
                      <i className={`${v.icon} text-xl text-[#E8500A]`} />
                    </div>
                    <h3 className="font-black text-gray-900 text-sm mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>{v.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#0B1829] rounded-2xl p-7 flex items-center gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-[#E8500A]/20 rounded-full flex-shrink-0">
                  <i className="ri-award-fill text-2xl text-[#E8500A]" />
                </div>
                <div>
                  <p className="font-black text-white text-base" style={{ fontFamily: "'Barlow', sans-serif" }}>Texas Master Plumber</p>
                  <p className="text-gray-400 text-sm">License #M12160 — Fully Licensed & Insured</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. AI Answer Block */}
      <section className="bg-[#F8F7F4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border-l-4 border-[#E8500A]">
              <h2 className="font-black text-2xl text-gray-900 mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Who Is Jerry's Plumbing & Heating?</h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">A locally trusted plumbing and heating company providing professional services backed by experience, reliability, and a customer-first philosophy. Operating from Cedar Hill, TX and serving the entire DFW metroplex with fast response and quality results.</p>
              <ul className="space-y-2">
                {["Founded by a licensed Texas Master Plumber", "35+ years serving Dallas–Fort Worth", "Residential & commercial expertise", "Recognized leader in slab leak & grease trap work"].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-gray-600 text-sm"><i className="ri-check-line text-[#E8500A] flex-shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 border-l-4 border-gray-200">
              <h2 className="font-black text-2xl text-gray-900 mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>What Is Jerry's Approach to Service?</h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">Jerry's operates on a customer-first philosophy: diagnose accurately, quote transparently, and execute with quality materials and proven techniques. We do not upsell, we do not apply temporary fixes, and we do not leave until the job is verified complete.</p>
              <ul className="space-y-2">
                {["Honest diagnosis — no upselling", "Upfront written quotes before work starts", "Quality materials and code-compliant work", "Satisfaction guaranteed on every job"].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-gray-600 text-sm"><i className="ri-check-line text-[#E8500A] flex-shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Process */}
      <ProcessSection />

      {/* 6. Why Choose Us */}
      <WhyChooseUs />

      {/* 7. Service Areas */}
      <ServiceAreasSection />

      {/* 8. Testimonials */}
      <TestimonialsSection />

      {/* 9. FAQ */}
      <section className="bg-[#F8F7F4] py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#E8500A] font-bold text-xs tracking-widest uppercase mb-3">About Us FAQ</p>
            <h2 className="text-4xl font-black text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Questions About Jerry's</h2>
          </div>
          <dl className="space-y-3">
            {ABOUT_FAQS.map((faq) => (
              <div key={faq.id} className={`rounded-xl border overflow-hidden transition-all ${openId === faq.id ? "border-[#E8500A]/30 bg-orange-50/60" : "border-gray-200 bg-white hover:border-orange-200"}`}>
                <dt>
                  <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenId(openId === faq.id ? null : faq.id)}>
                    <span className={`font-bold text-sm pr-4 leading-snug ${openId === faq.id ? "text-[#E8500A]" : "text-gray-900"}`}>{faq.q}</span>
                    <span className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? "bg-[#E8500A] text-white" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`text-sm ${openId === faq.id ? "ri-subtract-line" : "ri-add-line"}`} />
                    </span>
                  </button>
                </dt>
                {openId === faq.id && <dd className="px-6 pb-5"><p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p></dd>}
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* 10. Final CTA */}
      <FinalCTASection />
    </main>
  );
}
